import { useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Chip } from '@progress/kendo-react-buttons';
import { classNames } from '@progress/kendo-react-common';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
  removeAppliedFilter,
  selectAppliedFilters,
} from '../../../store/slices/common-filters.slice';
import {
  AutocompleteFilterOption,
  CommonFilterObject,
  DatasetIDComponentProp,
} from '../../../models/filter.model';
import {
  selectVisualType,
  selectVisualData,
  selectStartDate,
  selectEndDate,
} from '../../../store/slices/date-filter.slice';
import { DateFilterTab } from '../../../models/date-filter.model';
import { DATASET_INITIAL_ID, DATE_FORMAT } from '../../../constants';
import { toggleFilters } from '../../../store/slices/filter-builder.slice';
import { selectChartFilters } from '../../../store/slices/chart-filters.slice';
import { RootState } from '../../../store';
import {
  selectSavedFilterKey,
  selectSavedFiltersEntries,
} from '../../../store/slices/saved-filters.slice';
import { Tooltip } from 'react-tooltip';
import { formatAppDate, onlyUnique } from '../../../hooks/utils';
import { useFilterAttributes } from '../../../hooks/filters';

export const chipTextArray = (filter: AutocompleteFilterOption[], expand = false): string[] => {
  if (!filter) return [];
  if (filter.length <= 2 && !expand) {
    return filter.map((option: AutocompleteFilterOption) => option.name);
  }
  const valueClone = [...filter];
  const result: string[] = valueClone
    .splice(0, expand ? valueClone.length : 2)
    .map((option: AutocompleteFilterOption) => option.name);
  if (!expand) {
    result.push('+' + valueClone.length + ' more');
  }
  return result;
};

export function AppliedFiltersElsie({
  datasetId = DATASET_INITIAL_ID,
  showTooltip = true,
  removable = true,
  textClass = 'k-color-primary',
}: DatasetIDComponentProp) {
  const { pathname } = useLocation();
  const displayDateType = useAppSelector((root: RootState) => selectVisualType(root, datasetId));
  const displayDateVisualData = useAppSelector((root: RootState) =>
    selectVisualData(root, datasetId)
  );
  const startDate = useAppSelector((root: RootState) => selectStartDate(root, datasetId));
  const endDate = useAppSelector((root: RootState) => selectEndDate(root, datasetId));

  const selectedSavedFilter = useAppSelector((root: RootState) =>
    selectSavedFilterKey(root, datasetId)
  );
  const savedFilters = useAppSelector(selectSavedFiltersEntries);
  const filters = useAppSelector((root: RootState) => selectAppliedFilters(root, datasetId));
  const chartFilters = useAppSelector((root: RootState) => selectChartFilters(root, datasetId));

  const { convertAttributeKeyToDisplayName } = useFilterAttributes();

  const dispatch = useAppDispatch();
  const keys = useMemo(
    () =>
      [
        ...Object.keys(filters || {}),
        ...chartFilters.map((chartFilter) => chartFilter.propertyName),
      ].filter(onlyUnique),
    [chartFilters, filters]
  );

  const openFilters = useCallback(() => {
    dispatch(toggleFilters({ visible: true, datasetId }));
  }, [datasetId, dispatch]);

  const removeFilter = useCallback(
    (filterKey: string) => {
      dispatch(
        removeAppliedFilter({
          datasetId,
          filterKey: filterKey,
        })
      );
    },
    [datasetId, dispatch]
  );

  useEffect(() => {
    const pathLevels = pathname.split('/').filter((path) => path);
    if (pathLevels[3]?.toLowerCase() === 'detail' || pathLevels[3]?.toLowerCase() === 'compare') {
      removeFilter('Survey');
      removeFilter('Question');
      removeFilter('ResponseOption');
    }
  }, [pathname, removeFilter]);

  const dateString = useMemo(() => {
    switch (displayDateType) {
      case DateFilterTab.LAST:
        return `Last ${displayDateVisualData[0]} ${displayDateVisualData[1]}`;
      case DateFilterTab.SINCE:
        return `Since ${formatAppDate(startDate, DATE_FORMAT)}`;
      case DateFilterTab.BETWEEN:
        return `Between ${formatAppDate(startDate, DATE_FORMAT)} - ${formatAppDate(endDate, DATE_FORMAT)}`;
      default:
        return '';
    }
  }, [startDate, endDate, displayDateType, displayDateVisualData]);

  const appliedSavedFilter = useMemo(() => {
    return savedFilters?.find((filter) => filter.key === selectedSavedFilter);
  }, [savedFilters, selectedSavedFilter]);

  const unappliedSavedFiltersKeys = useMemo(() => {
    if (appliedSavedFilter?.value.filters) {
      return Object.keys(appliedSavedFilter?.value.filters).filter(
        (k) => keys.findIndex((key) => key === k) === -1
      );
    }
    return [];
  }, [appliedSavedFilter, keys]);
  return (
    <div className="applied-filters-elsie" style={{ background: 'white' }}>
      <div className="applied-filters-chip-list">
        <div className="applied-filter-children" title={dateString}>
          {!appliedSavedFilter && (
            <Chip fillMode={'outline'} onClick={() => openFilters()}>
              <span className="k-chip-label">
                <span>Date</span> <b className={textClass}>{dateString}</b>
              </span>
            </Chip>
          )}
        </div>
        {appliedSavedFilter && (
          <div className="applied-filter-children applied-saved-filter">
            <Chip fillMode={'outline'} onClick={() => openFilters()}>
              <span className="k-chip-label">
                <span>Date</span> <b>{dateString}</b>
              </span>
            </Chip>
          </div>
        )}
        {keys?.map((filterKey: string) => {
          const filter: CommonFilterObject = filters[filterKey];
          const chartFilter: CommonFilterObject = chartFilters.reduce(
            (chartFilterValue, chartFilter) => {
              if (chartFilter.propertyName === filterKey) {
                const { propertyName, propertyDisplayName } = chartFilter;
                const attributeLabel = convertAttributeKeyToDisplayName(chartFilter.propertyName);
                return {
                  value: [
                    {
                      name: chartFilter.propertyDisplayValue ?? chartFilter.value,
                      value: chartFilter.value,
                    },
                  ],
                  label: propertyDisplayName || attributeLabel || propertyName,
                };
              }
              return chartFilterValue;
            },
            {} as CommonFilterObject
          );

          const currentFilter = {
            label: () => {
              if (filter?.label) {
                if (filter.label === 'Category/Question/Response') {
                  return (
                    (filter?.value?.[0].label as string) ??
                    (filter?.value?.[0].filterType as string)
                  );
                }
              }
              return filter?.label ?? chartFilter?.label;
            },
            value: [...(filter?.value ?? []), ...(chartFilter?.value ?? [])],
          };

          const textArray = chipTextArray([...(currentFilter?.value ? currentFilter.value : [])]);
          const expanded = chipTextArray(
            [...(currentFilter?.value ? currentFilter.value : [])],
            true
          );

          return (
            <div
              data-tooltip-id={'filter-chip-' + filterKey}
              key={filterKey}
              className="applied-filter-children"
              title={
                showTooltip
                  ? currentFilter?.value
                      .map(({ name }: AutocompleteFilterOption) => name)
                      .join(', ')
                  : ''
              }
            >
              <Chip fillMode={'outline'}>
                <span className="k-chip-label">{currentFilter?.label()}&nbsp;</span>
                <b className={classNames({ [textClass]: !appliedSavedFilter })}>
                  {textArray.join(textArray.length > 1 ? ', ' : ' ')}
                </b>
              </Chip>

              {showTooltip && (
                <Tooltip
                  id={'filter-chip-' + filterKey}
                  place="top"
                  positionStrategy={'fixed'}
                  style={{ fontSize: '0.8rem', maxWidth: 300, zIndex: 9999 }}
                >
                  {expanded.join(', ')}
                </Tooltip>
              )}
            </div>
          );
        })}
        {unappliedSavedFiltersKeys.map((filterKey: string) => {
          const currentFilter = appliedSavedFilter?.value.filters[filterKey];
          const textArray = chipTextArray(currentFilter?.value);

          return (
            <div
              key={filterKey}
              className="applied-filter-children"
              title={currentFilter?.value
                .map(({ name }: AutocompleteFilterOption) => name)
                .join(', ')}
            >
              <Chip
                disabled
                removable={removable && !appliedSavedFilter}
                fillMode={'outline'}
                onRemove={(e) => {
                  e.syntheticEvent.stopPropagation();
                  e.syntheticEvent.preventDefault();
                  removeFilter(filterKey);
                }}
                onClick={openFilters}
              >
                <span className="k-chip-label">{currentFilter?.label}&nbsp;</span>
                <b className={classNames({ [textClass]: !appliedSavedFilter })}>
                  {textArray.join(textArray.length > 1 ? ', ' : ' ')}
                </b>
              </Chip>
            </div>
          );
        })}
      </div>
    </div>
  );
}
